import React from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

// Fix default marker icons (webpack breaks the built-in paths)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl:       "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl:     "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

// Gold pin — inline SVG data URL to match MAVULA brand
const goldPin = new L.Icon({
  iconUrl:
    "data:image/svg+xml;utf8," +
    encodeURIComponent(`
      <svg xmlns='http://www.w3.org/2000/svg' width='30' height='42' viewBox='0 0 30 42'>
        <defs>
          <filter id='s' x='-50%' y='-50%' width='200%' height='200%'>
            <feDropShadow dx='0' dy='2' stdDeviation='1.5' flood-opacity='0.35'/>
          </filter>
        </defs>
        <path filter='url(%23s)' d='M15 0C6.7 0 0 6.7 0 15c0 11 15 27 15 27s15-16 15-27C30 6.7 23.3 0 15 0z' fill='#D4AF37'/>
        <circle cx='15' cy='15' r='6.5' fill='#0A3B28'/>
      </svg>
    `),
  iconSize: [30, 42],
  iconAnchor: [15, 42],
  popupAnchor: [0, -34],
});

// Green pin for cluster / secondary
const greenPin = new L.Icon({
  iconUrl:
    "data:image/svg+xml;utf8," +
    encodeURIComponent(`
      <svg xmlns='http://www.w3.org/2000/svg' width='30' height='42' viewBox='0 0 30 42'>
        <path d='M15 0C6.7 0 0 6.7 0 15c0 11 15 27 15 27s15-16 15-27C30 6.7 23.3 0 15 0z' fill='#0A3B28'/>
        <circle cx='15' cy='15' r='6.5' fill='#D4AF37'/>
      </svg>
    `),
  iconSize: [30, 42],
  iconAnchor: [15, 42],
  popupAnchor: [0, -34],
});

const TILE_URL = "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png";
const ATTRIB = '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>';

/**
 * Single-location map for project detail + footer office.
 */
export function MapSingle({ lat, lng, label, height = 320, zoom = 14 }) {
  if (typeof lat !== "number" || typeof lng !== "number") return null;
  return (
    <div className="rounded-3xl overflow-hidden mv-hairline border" style={{ height }}>
      <MapContainer center={[lat, lng]} zoom={zoom} scrollWheelZoom={false} style={{ height: "100%", width: "100%" }}>
        <TileLayer url={TILE_URL} attribution={ATTRIB} />
        <Marker position={[lat, lng]} icon={goldPin}>
          {label && (
            <Popup>
              <div className="font-display text-[14px]">{label}</div>
            </Popup>
          )}
        </Marker>
      </MapContainer>
    </div>
  );
}

// Compute a fitting bounds & center from a list of points
function FitBounds({ points }) {
  const map = useMap();
  React.useEffect(() => {
    if (!points.length) return;
    const bounds = L.latLngBounds(points.map((p) => [p.lat, p.lng]));
    map.fitBounds(bounds, { padding: [40, 40], maxZoom: 8 });
  }, [points, map]);
  return null;
}

/**
 * Multi-location map for /projects and countries pages.
 * items: [{ lat, lng, title, subtitle, href }]
 */
export function MapMulti({ items = [], height = 480, defaultCenter = [-19, 26], defaultZoom = 4 }) {
  const points = items.filter((i) => typeof i.lat === "number" && typeof i.lng === "number");
  return (
    <div className="rounded-3xl overflow-hidden mv-hairline border" style={{ height }}>
      <MapContainer center={defaultCenter} zoom={defaultZoom} scrollWheelZoom={true} style={{ height: "100%", width: "100%" }}>
        <TileLayer url={TILE_URL} attribution={ATTRIB} />
        {points.length > 0 && <FitBounds points={points} />}
        {points.map((i, idx) => (
          <Marker key={idx} position={[i.lat, i.lng]} icon={i.pinStyle === "green" ? greenPin : goldPin}>
            <Popup>
              <div style={{ minWidth: 180 }}>
                <div className="font-display text-[15px]" style={{ color: "#0A3B28" }}>{i.title}</div>
                {i.subtitle && <div style={{ color: "#4A5568", fontSize: 12, marginTop: 2 }}>{i.subtitle}</div>}
                {i.href && (
                  <a href={i.href} style={{ color: "#0A3B28", fontSize: 12, fontWeight: 600, marginTop: 8, display: "inline-block", textDecoration: "underline" }}>
                    View →
                  </a>
                )}
              </div>
            </Popup>
          </Marker>
        ))}
      </MapContainer>
    </div>
  );
}
